import React, { useEffect, useMemo, useState } from "react";
import {
  Package,
  Clock,
  CheckCircle2,
  ShoppingBag,
  CalendarDays,
  ShieldCheck,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../Api/Api";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const user_id = localStorage.getItem("user_id");

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user_id) {
        setLoading(false);

        Swal.fire({
          icon: "warning",
          title: "Sign In Required",
          text: "Please sign in to view your orders.",
          confirmButtonColor: "#2563EB",
        });

        return;
      }

      try {
        const response = await api.get(
          `/get_product_orders/${user_id}/`
        );

        setOrders(
          Array.isArray(response.data?.orders)
            ? response.data.orders
            : []
        );
      } catch (error) {
        console.error("Failed to load orders:", error);

        Swal.fire({
          icon: "error",
          title: "Unable to Load Orders",
          text:
            error.response?.data?.message ||
            error.response?.data?.detail ||
            "We couldn't load your orders. Please try again.",
          confirmButtonColor: "#2563EB",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user_id]);

  const deliveredCount = useMemo(() => {
    return orders.filter((order) => order.delivered).length;
  }, [orders]);

  const pendingCount = useMemo(() => {
    return orders.filter((order) => !order.delivered).length;
  }, [orders]);

  const totalSpent = useMemo(() => {
    return orders.reduce((total, order) => {
      const price = Number(order.price || 0);
      const quantity = Number(order.quantity || 1);

      return total + price * quantity;
    }, 0);
  }, [orders]);

  const formatPrice = (price) => {
    return Number(price || 0).toLocaleString("en-KE");
  };

  const formatDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleString("en-KE", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <section className="relative bg-slate-950 text-white pt-20 pb-28 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[450px] h-[450px] bg-blue-600/20 rounded-full blur-[140px]" />

        <div className="absolute -bottom-40 right-0 w-[450px] h-[450px] bg-cyan-400/10 rounded-full blur-[130px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-6">
            <ShieldCheck size={15} />
            Order History
          </div>

          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            My Orders
          </h1>

          <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">
            Track your purchases, review order details, and stay updated on
            delivery progress.
          </p>
        </div>
      </section>

      {/* Main */}
      <main className="relative -mt-14 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Stats */}
          {!loading && orders.length > 0 && (
            <div className="grid md:grid-cols-3 gap-5 mb-8">
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5">
                  <ShoppingBag size={21} />
                </div>

                <p className="text-sm text-slate-500 mb-2">
                  Total Orders
                </p>

                <p className="text-3xl font-bold">
                  {orders.length}
                </p>
              </div>

              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-5">
                  <CheckCircle2 size={21} />
                </div>

                <p className="text-sm text-slate-500 mb-2">
                  Delivered
                </p>

                <p className="text-3xl font-bold">
                  {deliveredCount}
                </p>
              </div>

              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5">
                  <Package size={21} />
                </div>

                <p className="text-sm text-slate-500 mb-2">
                  Total Order Value
                </p>

                <p className="text-2xl md:text-3xl font-bold">
                  KES {formatPrice(totalSpent)}
                </p>
              </div>
            </div>
          )}

          {/* Orders Container */}
          <section className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 overflow-hidden">
            <div className="px-6 md:px-8 py-6 border-b border-slate-200 flex items-center justify-between gap-5">
              <div>
                <h2 className="text-xl font-bold">
                  Order history
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  Your recent purchases and delivery status.
                </p>
              </div>

              {!loading && orders.length > 0 && (
                <div className="hidden sm:flex items-center gap-2 text-sm text-slate-500">
                  <Clock size={16} className="text-blue-600" />
                  {pendingCount} pending
                </div>
              )}
            </div>

            {/* Loading */}
            {loading && (
              <div className="p-6 md:p-8 space-y-5">
                {[1, 2, 3, 4].map((item) => (
                  <div
                    key={item}
                    className="p-6 rounded-2xl border border-slate-200 animate-pulse"
                  >
                    <div className="flex gap-4">
                      <div className="w-14 h-14 rounded-xl bg-slate-200" />

                      <div className="flex-1">
                        <div className="h-5 bg-slate-200 rounded w-1/2 mb-3" />
                        <div className="h-3 bg-slate-200 rounded w-1/3 mb-2" />
                        <div className="h-3 bg-slate-200 rounded w-1/4" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading && orders.length === 0 && (
              <div className="py-20 px-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-6">
                  <ShoppingBag size={28} />
                </div>

                <h3 className="text-2xl font-bold mb-3">
                  No orders yet
                </h3>

                <p className="text-slate-500 max-w-md mx-auto mb-8 leading-relaxed">
                  You haven't placed any orders yet. Products you purchase
                  from the Nexindi Shop will appear here.
                </p>

                <a
                  href="/shop"
                  className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
                >
                  Visit Shop
                </a>
              </div>
            )}

            {/* Orders */}
            {!loading && orders.length > 0 && (
              <div className="divide-y divide-slate-100">
                {orders.map((order) => {
                  const quantity = Number(order.quantity || 1);
                  const unitPrice = Number(order.price || 0);
                  const subtotal = unitPrice * quantity;

                  return (
                    <article
                      key={order.id}
                      className="p-6 md:p-8 hover:bg-slate-50/70 transition"
                    >
                      <div className="flex flex-col md:flex-row md:items-center gap-6">
                        {/* Icon */}
                        <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                          <Package size={23} />
                        </div>

                        {/* Details */}
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                            <div>
                              <h3 className="text-lg md:text-xl font-bold text-slate-900">
                                {order.product_name}
                              </h3>

                              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 text-sm text-slate-500">
                                <span>
                                  Quantity:{" "}
                                  <strong className="text-slate-700">
                                    {quantity}
                                  </strong>
                                </span>

                                <span>
                                  Unit price:{" "}
                                  <strong className="text-slate-700">
                                    KES {formatPrice(unitPrice)}
                                  </strong>
                                </span>
                              </div>
                            </div>

                            <span
                              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-semibold w-fit ${
                                order.delivered
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-amber-50 text-amber-700"
                              }`}
                            >
                              <span
                                className={`w-2 h-2 rounded-full ${
                                  order.delivered
                                    ? "bg-emerald-500"
                                    : "bg-amber-500"
                                }`}
                              />

                              {order.delivered
                                ? "Delivered"
                                : "Pending"}
                            </span>
                          </div>

                          <div className="mt-5 pt-5 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                              <CalendarDays size={14} />

                              Ordered {formatDate(order.created_at)}
                            </div>

                            <div className="text-left sm:text-right">
                              <p className="text-xs text-slate-400 mb-1">
                                Order total
                              </p>

                              <p className="text-lg font-bold text-blue-600">
                                KES {formatPrice(subtotal)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default Orders;